// File: structural_app/frontend/src/components/BeamFormulae/BeamFormulaCalculator.js
// Purpose: This component serves as the main interface for the Beam Analysis tool.
// It manages the state for beam configuration (spans, loads, supports), cross-section selection,
// fetches necessary data, handles user input and validation, triggers backend calculations,
// and displays the beam visualization, input form, and calculation results.

import React, { useEffect, useRef, useCallback, useContext } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import BeamVisualization from './BeamVisualization';
import {
  SHSIcon,
  RHSIcon,
  HEAIcon,
  IPEIcon,
  RectConcreteIcon,
  TBeamIcon,
  TimberRectIcon,
  GlulamIcon,
} from '../CrossSections/CrossSectionIcons';
import BeamForm from './BeamForm';
import BeamContext, { buildBeamInputsKey } from '../../context/BeamContext';
import ResultsDisplay from './ResultsDisplay';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { parseLocaleFloat } from '../../utils/localeFloat';
import CalculationHistory from '../shared/CalculationHistory';

// Helper: Parse section name (Keep or remove if not needed here)
const parseSectionName = (name) => {
  const matchCFSHS = name.match(/^CFSHS(\d+)X([\d.]+)/);
  if (matchCFSHS) {
    const height = parseInt(matchCFSHS[1], 10);
    const thickness = parseFloat(matchCFSHS[2]);
    return { height, width: height, thickness };
  }
  const matchCFRHS = name.match(/^CFRHS(\d+)X(\d+)X([\d.]+)/);
  if (matchCFRHS) {
    const height = parseInt(matchCFRHS[1], 10);
    const width = parseInt(matchCFRHS[2], 10);
    const thickness = parseFloat(matchCFRHS[3]);
    return { height, width, thickness };
  }
  const matchHFSHS = name.match(/^HFSHS(\d+)X([\d.]+)/);
  if (matchHFSHS) {
    const height = parseInt(matchHFSHS[1], 10);
    const thickness = parseFloat(matchHFSHS[2]);
    return { height, width: height, thickness };
  }
  const matchNumericSuffix = name.match(/^([A-Z]+)(\d+)/);
   if (matchNumericSuffix) {
     const height = parseInt(matchNumericSuffix[2], 10);
     const typePrefix = matchNumericSuffix[1];
     const width = (typePrefix === 'HEA' || typePrefix === 'HEB' || typePrefix === 'UPE') ? height : 0;
     return { height, width: width, thickness: 0 };
   }
  return { height: 0, width: 0, thickness: 0 };
};

// Add formatCrossSectionName function
const formatCrossSectionName = (section) => {
  switch (section.type) {
    case 'CFRHS':
      return `CFRHS${section.height}X${section.width}X${section.thickness}`;
    case 'CFSHS':
      return `CFSHS${section.height}X${section.thickness}`;
    case 'HFSHS':
      return `HFSHS${section.height}X${section.thickness}`;
    case 'IPE':
      return `IPE${section.height}`;
    case 'HEA':
      return `HEA${section.width}`;
    default:
      return section.name;
  }
};

const BeamFormulaCalculator = () => <CalculatorUI />;

const CalculatorUI = () => {
  const beamSvgRef = useRef(null);
  const historyRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const context = useContext(BeamContext);
  const theme = useTheme();

  const {
    formErrors,
    selectedSection: selectedCrossSection,
    spans,
    setNotification,
    setError,
    setResults,
    setLoading,
    setLastSuccessfulCalcInputsKey,
    loading,
    crossSections,
    selectedCrossSectionId,
    selectedType,
    materialFamily,
    advancedOptions,
    loadBeamFromSavedInput,
    results,
    resultsOutOfDate,
    setSelectedType,
    setMaterialFamily,
    notification,
  } = context;

  useEffect(() => {
    const sid = searchParams.get('calcSession');
    if (!sid) return;
    let cancelled = false;
    axios
      .get(`/api/beam-formulae/saved-analyses/${sid}/`)
      .then((r) => {
        if (cancelled) return;
        const { input_data, results_data } = r.data;
        if (input_data) loadBeamFromSavedInput(input_data);
        if (results_data && Object.keys(results_data).length) {
          setResults(results_data);
          const ui = input_data?.ui || {};
          setLastSuccessfulCalcInputsKey(
            buildBeamInputsKey({
              spans: ui.spans ?? spans,
              advancedOptions: ui.advancedOptions ?? advancedOptions,
              selectedCrossSectionId: ui.selectedCrossSectionId ?? selectedCrossSectionId,
              materialFamily: ui.materialFamily ?? materialFamily,
              selectedType: ui.selectedType ?? selectedType,
            }),
          );
        }
        setSearchParams({}, { replace: true });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const getSavePayload = useCallback(() => {
    const cs = selectedCrossSection;
    return {
      input_data: {
        ui: {
          spans,
          selectedType,
          materialFamily,
          selectedCrossSectionId,
          advancedOptions,
        },
      },
      results_data: results,
      cross_section_type:
        cs?.type || (materialFamily === 'concrete' ? 'concrete' : materialFamily === 'wood' ? 'WOOD' : ''),
      cross_section_name: cs?.name || cs?.section_name || '',
    };
  }, [
    spans,
    selectedType,
    materialFamily,
    selectedCrossSectionId,
    advancedOptions,
    results,
    selectedCrossSection,
  ]);

  const handleCalculate = useCallback(async () => {
    console.log('handleCalculate invoked. Context details from closure:', {
        isLoadingSections: loading.sections,
        formErrors: JSON.stringify(formErrors),
        selectedCrossSectionObj: selectedCrossSection,
        currentSelectedId: selectedCrossSectionId,
        availableCrossSectionsCount: crossSections ? crossSections.length : 0
    });

    if (loading && loading.sections) {
        setNotification({
            open: true,
            message: 'Cross-sections are still loading, please wait.',
            severity: 'info'
        });
        return;
    }

    if (Object.keys(formErrors).length > 0) {
        setNotification({
            open: true,
            message: 'Please fix the errors in the form before calculating.',
            severity: 'error'
        });
        setResults(null);
        return;
    }
    setError(prev => ({ ...prev, calculation: null }));

    if (!selectedCrossSection) {
      setNotification({ open: true, message: 'Please select a cross section', severity: 'error' });
      return;
    }

    const validSpans = spans.filter((span) => parseLocaleFloat(span.length) > 0);
    if (validSpans.length === 0) {
       setNotification({ open: true, message: 'Please define at least one span with a positive length.', severity: 'error' });
       return;
    }

    const totalBeamLen = validSpans.reduce((sum, sp) => sum + (parseLocaleFloat(sp.length) || 0), 0);

    const ao = advancedOptions || {};
    const ec3SteelLtb = materialFamily === 'steel' && !!ao.enableEc3Checks;
    const nSpans = validSpans.length;
    const calculationData = {
      loads_basis: ao.loadsBasis === 'characteristic' ? 'characteristic' : 'design',
      spans: validSpans.map((span, index) => {
          const isFirst = index === 0;
          const isLast = index === nSpans - 1;
          // Always send support flags to the 14-DOF / LTB backend (do not tie to EC3 checks toggle).
          const leftTorsion =
            span.leftTorsionSupport !== undefined
              ? span.leftTorsionSupport !== false
              : span.leftLtbSupport !== undefined
                ? span.leftLtbSupport !== false
                : true;
          const rightTorsion =
            span.rightTorsionSupport !== undefined
              ? span.rightTorsionSupport !== false
              : span.rightLtbSupport !== undefined
                ? span.rightLtbSupport !== false
                : true;
          const leftBoundary = {
            type: span.leftBoundary,
            torsion_support: leftTorsion,
            ltb_support: leftTorsion,
          };
          if (materialFamily === 'steel') {
            leftBoundary.warping_support = isFirst && span.leftWarpingSupport !== false;
          }
          if (span.leftBoundary === 'spring' && span.leftSpringCoefficient !== '') {
              leftBoundary.stiffness = parseLocaleFloat(span.leftSpringCoefficient || 0);
          }
          if (span.leftBoundary === 'spring' && ec3SteelLtb) {
              const b = parseLocaleFloat(span.leftSpringCoefficient || '');
              const ax = parseLocaleFloat(span.leftSpringAxial || '');
              const tor = parseLocaleFloat(span.leftSpringTorsion || '');
              const w = parseLocaleFloat(span.leftSpringWarping || '');
              if ([b, ax, tor, w].some((x) => Number.isFinite(x) && x !== 0)) {
                leftBoundary.spring_stiffnesses = {
                  bending_my_knm_per_rad: Number.isFinite(b) ? b : 0,
                  axial_kn_per_m: Number.isFinite(ax) ? ax : 0,
                  torsion_knm_per_rad: Number.isFinite(tor) ? tor : 0,
                  warping_knm2_per_rad: Number.isFinite(w) ? w : 0,
                };
              }
          }
          const rightBoundary = {
            type: span.rightBoundary,
            torsion_support: rightTorsion,
            ltb_support: rightTorsion,
          };
          if (materialFamily === 'steel') {
            rightBoundary.warping_support = isLast && span.rightWarpingSupport !== false;
          }
          if (span.rightBoundary === 'spring' && span.rightSpringCoefficient !== '') {
              rightBoundary.stiffness = parseLocaleFloat(span.rightSpringCoefficient || 0);
          }
          if (span.rightBoundary === 'spring' && ec3SteelLtb) {
              const b = parseLocaleFloat(span.rightSpringCoefficient || '');
              const ax = parseLocaleFloat(span.rightSpringAxial || '');
              const tor = parseLocaleFloat(span.rightSpringTorsion || '');
              const w = parseLocaleFloat(span.rightSpringWarping || '');
              if ([b, ax, tor, w].some((x) => Number.isFinite(x) && x !== 0)) {
                rightBoundary.spring_stiffnesses = {
                  bending_my_knm_per_rad: Number.isFinite(b) ? b : 0,
                  axial_kn_per_m: Number.isFinite(ax) ? ax : 0,
                  torsion_knm_per_rad: Number.isFinite(tor) ? tor : 0,
                  warping_knm2_per_rad: Number.isFinite(w) ? w : 0,
                };
              }
          }
          const _knownSupport = (t) =>
            ['fixed', 'pinned', 'rolling_pinned', 'spring', 'free', 'roller'].includes(String(t || ''));
          if (index > 0 && !_knownSupport(leftBoundary.type)) {
              leftBoundary.type = 'pinned';
          }
          if (index < validSpans.length - 1 && !_knownSupport(rightBoundary.type)) {
              rightBoundary.type = 'pinned';
          }
          const spanPayload = {
            length: parseLocaleFloat(span.length),
            left_boundary: leftBoundary,
            right_boundary: rightBoundary,
            leftSpringCoefficient: span.leftBoundary === 'spring' && span.leftSpringCoefficient !== ''
              ? parseLocaleFloat(span.leftSpringCoefficient) : undefined,
            rightSpringCoefficient: span.rightBoundary === 'spring' && span.rightSpringCoefficient !== ''
              ? parseLocaleFloat(span.rightSpringCoefficient) : undefined,
            loads: span.loads.map(load => {
                const parseEccMm = (raw) => {
                    const n = parseLocaleFloat(raw ?? '0');
                    return Number.isFinite(n) ? n * 1e-3 : 0;
                };
                // Prefer UI mm fields whenever they are present (even "0"). If we prefer snake_case
                // eccentricity_y when it exists as 0, we ignore the user's eccentricityY (e.g. 1000 mm)
                // after any merge/save that added eccentricity_y: 0 to the load object.
                const mmY = load.eccentricityY;
                const mmZ = load.eccentricityZ;
                const eccY =
                  mmY !== undefined && mmY !== null && String(mmY).trim() !== ''
                    ? parseEccMm(mmY)
                    : (() => {
                        const sy = parseLocaleFloat(load.eccentricity_y ?? '');
                        return Number.isFinite(sy) ? sy : 0;
                      })();
                const eccZ =
                  mmZ !== undefined && mmZ !== null && String(mmZ).trim() !== ''
                    ? parseEccMm(mmZ)
                    : (() => {
                        const sz = parseLocaleFloat(load.eccentricity_z ?? '');
                        return Number.isFinite(sz) ? sz : 0;
                      })();
                const baseLoadData = {
                    type: load.type === 'line' ? 'udl' : load.type,
                    magnitude: -Math.abs(parseLocaleFloat(load.magnitude || 0)),
                    load_action: load.loadAction || 'permanent',
                    eccentricity_y: eccY,
                    eccentricity_z: eccZ,
                };
                if (baseLoadData.type === 'udl') {
                    const isLocal = load.coordinateSystem === 'local';
                    const spanLen = parseLocaleFloat(span.length) || 0;
                    const parseEdge = (raw) => {
                        if (raw === '' || raw == null) return null;
                        const n = parseLocaleFloat(raw);
                        return Number.isFinite(n) ? n : null;
                    };
                    let start = parseEdge(load.startPosition);
                    let end = parseEdge(load.endPosition);
                    if (start === null) start = 0;
                    if (end === null) end = (isLocal ? spanLen : totalBeamLen);
                    return {
                      ...baseLoadData,
                      start,
                      end,
                      coordinateSystem: isLocal ? 'local' : 'global',
                    };
                }
                return { ...baseLoadData, position: parseLocaleFloat(load.position || 0) };
            }).filter(load => !isNaN(load.magnitude) && Math.abs(load.magnitude) > 1e-9)
          };
          return spanPayload;
      })
    };

    if (materialFamily === 'steel') {
      calculationData.cross_section_id = selectedCrossSection.id;
      calculationData.cross_section_type = selectedCrossSection.type;
      calculationData.cross_section_name = selectedCrossSection.name;
      calculationData.structural_steel_grade = ao.steelGrade || 'S355';
      if (ao.steelEOverrideGpa) {
        const e = parseLocaleFloat(ao.steelEOverrideGpa);
        if (!Number.isNaN(e)) calculationData.steel_e_gpa = e;
      }
      const ubStr = ao.unbracedLengthM != null ? String(ao.unbracedLengthM).trim() : '';
      if (ubStr !== '') {
        calculationData.unbraced_length_m = ubStr;
      }
      const ltbStr = ao.ltbEffectiveLengthM != null ? String(ao.ltbEffectiveLengthM).trim() : '';
      if (ltbStr !== '') {
        calculationData.ltb_effective_length_m = ltbStr;
      }
      const nEdStr = ao.N_Ed_kN != null ? String(ao.N_Ed_kN).trim() : '';
      if (nEdStr !== '') {
        const nEd = parseLocaleFloat(nEdStr);
        if (!Number.isNaN(nEd) && nEd > 0) calculationData.N_Ed_kN = nEd;
      }
      const lcyStr = ao.L_cr_y_m != null ? String(ao.L_cr_y_m).trim() : '';
      if (lcyStr !== '') {
        const lcy = parseLocaleFloat(lcyStr);
        if (!Number.isNaN(lcy) && lcy > 0) calculationData.L_cr_y_m = lcy;
      }
      const lczStr = ao.L_cr_z_m != null ? String(ao.L_cr_z_m).trim() : '';
      if (lczStr !== '') {
        const lcz = parseLocaleFloat(lczStr);
        if (!Number.isNaN(lcz) && lcz > 0) calculationData.L_cr_z_m = lcz;
      }
      calculationData.enable_ec3_checks = !!ao.enableEc3Checks;
      calculationData.self_weight = !!ao.selfWeight;
      const ps2 = parseLocaleFloat(ao.psi2);
      if (!Number.isNaN(ps2)) calculationData.psi2 = ps2;
      const dlr = parseLocaleFloat(ao.deflectionLimitRatio);
      if (!Number.isNaN(dlr)) calculationData.deflection_limit_ratio = dlr;
    } else if (materialFamily === 'concrete') {
      const shape = String(ao.concreteSectionShape || 'rectangular').toLowerCase();
      calculationData.cross_section_type = 'concrete';
      calculationData.concrete_section_shape = shape;
      const parseDim = (raw) => {
        const n = parseLocaleFloat(raw ?? '');
        return Number.isFinite(n) ? n : NaN;
      };
      if (shape === 'inverted_t') {
        const bf = parseDim(ao.invertedTbfM);
        const hf = parseDim(ao.invertedThfM);
        const bw = parseDim(ao.invertedTbwM);
        const hw = parseDim(ao.invertedThwM);
        if (![bf, hf, bw, hw].every((v) => v > 0)) {
          setNotification({
            open: true,
            message: 'Inverted-T section: enter positive flange width, flange thickness, web width, and web height.',
            severity: 'error',
          });
          return;
        }
        calculationData.inverted_t_bf_m = bf;
        calculationData.inverted_t_hf_m = hf;
        calculationData.inverted_t_bw_m = bw;
        calculationData.inverted_t_hw_m = hw;
      } else {
        const wParsed = parseLocaleFloat(ao.concreteWidthM ?? '');
        const hParsed = parseLocaleFloat(ao.concreteHeightM ?? '');
        const w = Number.isFinite(wParsed)
          ? wParsed
          : (selectedCrossSection.width_m != null ? selectedCrossSection.width_m : 0.3);
        const h = Number.isFinite(hParsed)
          ? hParsed
          : (selectedCrossSection.height_m != null ? selectedCrossSection.height_m : 0.5);
        calculationData.width_m = w;
        calculationData.height_m = h;
      }
      calculationData.concrete_grade = ao.concreteGrade || 'C30/37';
      calculationData.steel_grade = ao.concreteSteelGrade || 'B500B';
      calculationData.cover_m = parseLocaleFloat(ao.concreteCoverM || '0.03');
      calculationData.analysis_type = ao.analysisType || 'linear';
      calculationData.exposure_class = ao.exposureClass || 'XC1';
      calculationData.enable_ec2_checks = ao.enableEc2Checks !== false;
      calculationData.concrete_auto_rebar = !!ao.concreteAutoRebar;
      calculationData.main_bar_count = parseInt(ao.concreteBarCount || '2', 10);
      calculationData.main_bar_diam_mm = parseLocaleFloat(ao.concreteBarDiamMm || '16');
      const ps2c = parseLocaleFloat(ao.psi2);
      if (!Number.isNaN(ps2c)) calculationData.psi2 = ps2c;
      const dlrc = parseLocaleFloat(ao.deflectionLimitRatio);
      if (!Number.isNaN(dlrc)) calculationData.deflection_limit_ratio = dlrc;
      calculationData.self_weight = !!ao.selfWeight;
      const pt = String(ao.concretePrestressType || 'none').toLowerCase();
      const nTen = parseInt(String(ao.prestressNTendons ?? 0), 10);
      const nTendons = Number.isNaN(nTen) ? 0 : Math.min(500, Math.max(0, nTen));
      if (pt !== 'none' && nTendons > 0) {
        const sig = parseLocaleFloat(ao.prestressTensionMpa ?? '1000');
        const cNom = parseLocaleFloat(String(ao.prestressTendonCoverMm ?? 30));
        calculationData.concrete_prestress_input = {
          tendon_preset: String(ao.prestressTendonPreset || '12.7-1860'),
          n_tendons: nTendons,
          sigma_pe_mpa: Number.isFinite(sig) ? sig : 1000,
          tendon_c_nom_mm: Number.isFinite(cNom) ? cNom : 30,
        };
      }
      if (['none', 'pretensioned', 'post_tensioned'].includes(pt)) {
        calculationData.concrete_prestress_type = pt;
      }
    } else if (materialFamily === 'wood') {
      calculationData.cross_section_type = 'WOOD';
      calculationData.cross_section_name = selectedCrossSection.section_name || selectedCrossSection.name;
      calculationData.timber_strength_class = ao.timberStrengthClass || 'C24';
      calculationData.timber_service_class = String(ao.timberServiceClass || '1');
      calculationData.load_duration_class = ao.loadDurationClass || 'medium_term';
      calculationData.enable_ec5_checks = !!ao.enableEc5Checks;
      const ps2w = parseLocaleFloat(ao.psi2);
      if (!Number.isNaN(ps2w)) calculationData.psi2 = ps2w;
      const dlrW = parseLocaleFloat(ao.deflectionLimitRatio);
      if (!Number.isNaN(dlrW)) calculationData.deflection_limit_ratio = dlrW;
      calculationData.self_weight = !!ao.selfWeight;
      const ltbWood = String(ao.ltbEffectiveLengthM || '').trim();
      if (ltbWood) calculationData.ltb_effective_length_m = ltbWood;
    }

    calculationData.eurocode_national_annex = ao.eurocodeNationalAnnex === 'nl' ? 'nl' : 'finland';

    console.log("Triggering FEA calculation with data:", JSON.stringify(calculationData, null, 2));

    setLoading(prev => ({ ...prev, calculation: true }));
    setError(prev => ({ ...prev, calculation: null }));
    setResults(null);
    try {
      const response = await axios.post('/api/beam-formulae/calculations/calculate-fea/', calculationData);
      console.log("FEA Calculation successful, response:", response.data);
      setResults(response.data.results);
      setLastSuccessfulCalcInputsKey(
        buildBeamInputsKey({
          spans,
          advancedOptions: ao,
          selectedCrossSectionId,
          materialFamily,
          selectedType,
        }),
      );
      setNotification({ open: true, message: 'FEA Calculation successful!', severity: 'success' });
      historyRef.current?.saveAuto?.({ results_data: response.data.results });
    } catch (err) {
      console.error("FEA Calculation failed:", err.response?.data || err.message);
      const errorMsg = err.response?.data?.error || err.message || 'FEA Calculation failed';
      setError(prev => ({ ...prev, calculation: errorMsg }));
      setNotification({ open: true, message: errorMsg, severity: 'error' });
      setResults(null);
      setLastSuccessfulCalcInputsKey(null);
    } finally {
      setLoading(prev => ({ ...prev, calculation: false }));
    }
  }, [
    formErrors, selectedCrossSection, spans, setNotification, setError, setResults, setLoading,
    setLastSuccessfulCalcInputsKey, loading, crossSections, selectedCrossSectionId, selectedType,
    materialFamily, advancedOptions,
  ]);

  const triggerCalculation = () => {
    handleCalculate();
  };

  /** Sticky sketch while there are no fresh results so the user keeps context when scrolling the form. */
  const beamVizSticky = results == null || resultsOutOfDate;
  const stickyTop = theme.spacing(10);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h4" component="h1">
          Beam Calculator
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CalculationHistory
            ref={historyRef}
            apiEndpoint="/api/beam-formulae/saved-analyses"
            getSavePayload={getSavePayload}
            appLabel="Beam calculator"
            onLoad={({ input_data, results_data }) => {
              if (input_data) loadBeamFromSavedInput(input_data);
              if (results_data && Object.keys(results_data).length) {
                setResults(results_data);
                const ui = input_data?.ui || {};
                setLastSuccessfulCalcInputsKey(
                  buildBeamInputsKey({
                    spans: ui.spans ?? spans,
                    advancedOptions: ui.advancedOptions ?? advancedOptions,
                    selectedCrossSectionId: ui.selectedCrossSectionId ?? selectedCrossSectionId,
                    materialFamily: ui.materialFamily ?? materialFamily,
                    selectedType: ui.selectedType ?? selectedType,
                  }),
                );
              }
            }}
            renderSummary={(item) => (
              <Typography variant="caption" color="text.secondary">
                {[item.cross_section_type, item.cross_section_name].filter(Boolean).join(' ') || '—'}
              </Typography>
            )}
          />
        </Box>
      </Box>
      <Grid container spacing={3}>
        {/* Left Panel: Visualization + Results */}
        <Grid item xs={12} md={5} lg={4}>
          {/*
            One sticky block (viz + results) when outputs are missing or stale, so the folded
            results header scrolls with the sketch instead of sitting below a separately sticky card.
          */}
          <Box
            sx={{
              width: '100%',
              ...(beamVizSticky
                ? {
                    // Do not set overflow/maxHeight on the sticky element — that breaks viewport
                    // sticky in many browsers and traps the sketch so it never scrolls with the page.
                    position: 'sticky',
                    top: stickyTop,
                    zIndex: theme.zIndex.appBar - 1,
                  }
                : {}),
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 2,
                mb: beamVizSticky ? 1 : 3,
                height: 'auto',
                minHeight: '250px',
              }}
            >
              <Typography variant="h6" gutterBottom>Beam Visualization</Typography>
              <BeamVisualization ref={beamSvgRef} />
            </Paper>
            <ResultsDisplay stackWithVisualization={beamVizSticky} beamSvgRef={beamSvgRef} />
          </Box>
        </Grid>

        {/* Right Panel: Form */}
        <Grid item xs={12} md={7} lg={8} className="right-panel">
            <Paper elevation={1} sx={{ p: 1, mb: 2 }}>
                <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Material</InputLabel>
                        <Select
                            label="Material"
                            value={materialFamily}
                            onChange={(e) => setMaterialFamily(e.target.value)}
                        >
                            <MenuItem value="steel">Steel</MenuItem>
                            <MenuItem value="concrete">Concrete</MenuItem>
                            <MenuItem value="wood">Timber</MenuItem>
                        </Select>
                    </FormControl>
                    {materialFamily === 'steel' && (
                        <>
                            <Tooltip title="Cold Formed SHS">
                                <IconButton size="small" onClick={() => setSelectedType('CFSHS')} color={selectedType === 'CFSHS' ? 'primary' : 'default'}><SHSIcon /></IconButton>
                            </Tooltip>
                            <Tooltip title="Cold Formed RHS">
                                <IconButton size="small" onClick={() => setSelectedType('CFRHS')} color={selectedType === 'CFRHS' ? 'primary' : 'default'}><RHSIcon /></IconButton>
                            </Tooltip>
                            <Tooltip title="Hot Formed SHS">
                                <IconButton size="small" onClick={() => setSelectedType('HFSHS')} color={selectedType === 'HFSHS' ? 'primary' : 'default'}><SHSIcon /></IconButton>
                            </Tooltip>
                            <Tooltip title="HEA Sections">
                                <IconButton size="small" onClick={() => setSelectedType('HEA')} color={selectedType === 'HEA' ? 'primary' : 'default'}><HEAIcon /></IconButton>
                            </Tooltip>
                            <Tooltip title="IPE Sections">
                                <IconButton size="small" onClick={() => setSelectedType('IPE')} color={selectedType === 'IPE' ? 'primary' : 'default'}><IPEIcon /></IconButton>
                            </Tooltip>
                        </>
                    )}
                    {materialFamily === 'concrete' && (
                        <>
                            <Tooltip title="Rectangular RC">
                                <IconButton size="small" color="primary"><RectConcreteIcon /></IconButton>
                            </Tooltip>
                            <Tooltip title="T-beam (geometry via advanced options)">
                                <IconButton size="small"><TBeamIcon /></IconButton>
                            </Tooltip>
                        </>
                    )}
                    {materialFamily === 'wood' && (
                        <>
                            <Tooltip title="Sawn timber">
                                <IconButton size="small" color="primary"><TimberRectIcon /></IconButton>
                            </Tooltip>
                            <Tooltip title="Glulam">
                                <IconButton size="small"><GlulamIcon /></IconButton>
                            </Tooltip>
                        </>
                    )}
                </Box>
            </Paper>
            <Paper elevation={3} sx={{ p: 2 }}>
              <BeamForm onSubmit={triggerCalculation} />
            </Paper>
        </Grid>
      </Grid>
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={() => setNotification({ ...notification, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default BeamFormulaCalculator; 